import { isFeatureExpired } from './SelectionDeadlineUtility';
import IsNullOrUndefined from "../utils/ObjectUtils";
import IsNullOrEmpty from "../utils/StringUtils";
import {CategoryType, FeatureType} from "../model/constants";

// A flagged category's conflicting partner is considered "still unresolved" (i.e. also
// unselected) when it can't be found, or it is found but has no real feature selected either.
// If the partner already has a real selection, this category must not be shown in the shot at all.
const isConflictingPartnerUnselected = (foundCategory, categories) => {
    const conflictingCategory = categories.find(c => c.id === foundCategory.conflict_category_id);
    return IsNullOrUndefined(conflictingCategory) || conflictingCategory.selected_feature === 0;
};

// A conversion-to-shower category starts with a bathtub when unselected (and vice versa).
// Use the configured conversion features to identify the two starting states, not category IDs or labels.
const ownsStartingLayout = (category, categories) => {
    const partner = categories.find(c => c.id === category.conflict_category_id && c.roomId === category.roomId);
    // Unit restrictions identify which starting-state category applies. A mixed group
    // may contain both bathtub screens and shower conversions, so conversion mappings
    // alone cannot identify its unselected state.
    if (partner && !!category.disabled !== !!partner.disabled) return !category.disabled;
    const layouts = categories.filter(c => c.roomId === category.roomId && c.bathroom_layout).map(c => c.bathroom_layout);
    const initialLayouts = [...new Set(layouts.map(layout => layout.initialLayout))];
    if (partner && initialLayouts.length === 1 && ['Bathtub', 'Shower'].includes(initialLayouts[0])) {
        const showerConversions = layouts.flatMap(layout => layout.showerConversionIds || []);
        const bathtubConversions = layouts.flatMap(layout => layout.bathtubConversionIds || []);
        const startingLayout = (candidate) => {
            const toShower = candidate.features.some(f => showerConversions.includes(f.id));
            const toBathtub = candidate.features.some(f => bathtubConversions.includes(f.id));
            if (toShower === toBathtub) return null;
            return toShower ? 'Bathtub' : 'Shower';
        };
        let own = startingLayout(category);
        let other = startingLayout(partner);
        if (own && !other) other = own === 'Bathtub' ? 'Shower' : 'Bathtub';
        if (other && !own) own = other === 'Bathtub' ? 'Shower' : 'Bathtub';
        if (own && other && own !== other) return own === initialLayouts[0];
    }
    // Preserve legacy behavior for non-bathroom pairs, missing mappings or contradictory flags.
    return category.id < category.conflict_category_id;
};

export const CameraShotUtility = {
    getCameraShot: (projectId, room, currentCamera, categories) =>
    {
        let categoriesSelectionString = '';
        const sectionSeparator = '.';
        const categorySelectionSeparator = '-';
        const shotCategories = [];

        if(IsNullOrUndefined(currentCamera)) return;

        const foundRoomCamera = room.cameras.find(c => c.id === currentCamera.id);

        if (!IsNullOrUndefined(foundRoomCamera) && !IsNullOrEmpty(foundRoomCamera.categories))
        {
            foundRoomCamera.categories.forEach(cameraCategory => {
                const foundCategory = categories.find(c => c.id === cameraCategory.id);

                // foundCategory can be undefined when the camera references a category that no
                // longer exists in the current categories list (e.g. it was removed by an
                // improvement rule). Guard against it before reading its properties, otherwise
                // this throws "Cannot read properties of undefined (reading 'selected_feature')".
                if (IsNullOrUndefined(foundCategory)) return;

                // Expired standard choices remain unselected, but the scene still needs a base
                // finish to address an existing render. This affects naming only, never the
                // customer's selection, price or save payload.
                const expiredStandard = foundCategory.selected_feature === 0
                    && foundCategory.look_type === FeatureType.Default
                    && foundCategory.features.length > 0
                    && foundCategory.features.every(f => isFeatureExpired(f));
                const baseFeature = expiredStandard
                    ? foundCategory.features.find(f => f.isDefault) || foundCategory.features[0] : null;
                const renderSelection = baseFeature ? baseFeature.id : foundCategory.selected_feature;
                const foundFeature = cameraCategory.features.find(f => f.id === renderSelection);
                if (!IsNullOrUndefined(foundFeature) && renderSelection !== 0){
                    shotCategories.push({ ...foundCategory, selected_feature: renderSelection });
                } else if (foundCategory.selected_feature === 0 && !IsNullOrUndefined(foundCategory.conflict_category_id)
                    && isConflictingPartnerUnselected(foundCategory, categories)
                    && ownsStartingLayout(foundCategory, categories)) {
                    // Keep the unselected starting state identifiable without selecting or charging
                    // for an improvement. The renderer uses the same "0" + category ID convention.
                    shotCategories.push({ ...foundCategory, selected_feature: '0' + foundCategory.id });
                }
            });
        }

        shotCategories.sort(
            function (a, b) {
                // Compare as strings explicitly: selected_feature is normally a number for a
                // real feature id, but a conflicting-category marker is the string "0"+id (e.g.
                // "030236"). Comparing a number against that string with < / > coerces the
                // string back to a number (dropping the leading zero), which broke the intended
                // ordering and pushed the marker to the end instead of the beginning. Forcing a
                // string comparison here matches the .NET tool's CategoriesSelection.OrderBy(cs
                // => cs.FeatureId), which already sorts the marker first since it operates on
                // string FeatureId values.
                const aValue = String(a.selected_feature);
                const bValue = String(b.selected_feature);

                if (aValue < bValue) {
                    return -1;
                }
                if (aValue > bValue) {
                    return 1;
                }

                return 0;
            });

        for (let countSelection = 0; countSelection < shotCategories.length; countSelection++) {
            categoriesSelectionString += shotCategories[countSelection].selected_feature;

            if (countSelection < shotCategories.length - 1) {
                categoriesSelectionString += categorySelectionSeparator;
            }
        }

        const currentCameraName = !IsNullOrUndefined(currentCamera.name) ? currentCamera.name : currentCamera.id;
        return projectId + sectionSeparator + currentCameraName + sectionSeparator + categoriesSelectionString;
    },

    updateCameraShots: (projectId, currentRoom, currentCamera, categories) =>
    {
        const currentRoomCameras = currentRoom.cameras;
        let currentRoomShots = [];

        currentRoomCameras.forEach((camera, index) => {
            let shot = CameraShotUtility.getCameraShot(projectId, currentRoom, camera, categories);
            currentRoomShots.push(shot);
        });

        return currentRoomShots;
    },

    findBestCameraForCategory: (focusedCategory, currentRoom, currentCamera, usePriority) => {

        if(focusedCategory !== null && currentCamera !== undefined) {
            let currentRoomCamerasRenderingCategory = [];
            let foundCamera = null;

            currentRoom.cameras.forEach(camera => {
                const cameraWithCategory = camera.categories.filter(c => c.id === focusedCategory.id)[0] || null;
                if (cameraWithCategory !== null) {
                    currentRoomCamerasRenderingCategory.push(camera);
                }
            });

            const currentCameraRenderingCategory = currentRoomCamerasRenderingCategory.filter(c => c.id === currentCamera.id)[0] || null;

            if (currentCameraRenderingCategory !== null)
            {
                return currentCamera;
            } else {
                // If no camera in the room renders this category at all, there is no "better"
                // camera to switch to, so keep the current one instead of forcing a jump back
                // to the room's first camera.
                return currentRoomCamerasRenderingCategory.length > 0
                    ? currentRoomCamerasRenderingCategory[0]
                    : currentCamera;
            }
        }

        return currentRoom.cameras[0];
    },
    IsIconOnly: (rooms, improvementRules, category, feature) => {
        let isIconOnly = true;

        if (category.look_type === FeatureType.Default && category.features.length === 1) {
            return false;
        }

        // Check if the feature is used in any room camera
        const isFeatureUsed = rooms.some(room =>
            room.cameras.some(roomCamera =>
                roomCamera.categories.some(c =>
                    c.id === category.id &&
                    c.features.some(f => f.id === feature.id)
                )
            )
        );

        if (isFeatureUsed) {
            return false; // Directly return false if feature is used
        }

        // If category is an Improvement type, check improvement rules
        if (category.look_type === CategoryType.Improvement) {
            // Find all rules where this category is the origin
            const relatedRules = improvementRules.filter(rule => rule.originId === category.id);

            // Check if any of the linked destination categories are present in cameras
            const isLinkedToBaseCategory = relatedRules.some(rule =>
                rooms.some(room =>
                    room.cameras.some(roomCamera =>
                        roomCamera.categories.some(c => c.id === rule.destinationId)
                    )
                )
            );

            if (isLinkedToBaseCategory) {
                return false; // It is not icon-only if it is linked to a base category
            }
        }

        // If no conditions met, it is icon-only
        return isIconOnly;
    }
};
